// LeadManagement.jsx — Hybrid Responsive (Desktop Table + Mobile Cards)

import React, { useState, useMemo, useCallback } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Modal,
  Row,
  Col,
  Card,
  Badge,
  Tab,
  Tabs,
  Alert,
  Pagination,
} from "react-bootstrap";

import {
  FaUser,
  FaTools,
  FaStickyNote,
  FaPhone,
  FaPlus,
  FaSearch,
  FaClock,
  FaPlay,
  FaSpinner,
  FaUpload,
  FaDownload,
  FaFilter,
  FaEdit,
  FaTrash,
  FaStar,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaFileAlt,
  FaExclamationTriangle,
  FaQuestionCircle,
  FaArrowRight,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";
import CSVUploader from "../components/CSVUploader";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "../context/AppContext";
import "../styles/LeadManagement.css";
import { leadsAPI, batchCallsAPI, requestReviewAPI } from "../services/api";
//use effect import
import { useEffect } from "react";

export const LEADS_QUERY_KEY = ["leadsData"];

// Keep this in sync with backend LeadPagination.page_size
const PAGE_SIZE = 100;

// Fetch paginated leads (uses getLeadsPaginated from your api.js)
export const fetchLeads = async ({ page = 1, status, search }) => {
  const res = await leadsAPI.getLeadsPaginated({
    page,
    status: status || undefined,
    search: search || undefined,
  });
  return res.data;
};

// Fetch leads by category/status
export const fetchLeadsByCategory = async ({ status, page = 1 }) => {
  const res = await leadsAPI.getLeadsPaginated({ status, page });
  return res.data;
};

const cleanPayload = (obj) => {
  const cleaned = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== "" && obj[key] !== null && obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
};

const DEFAULT_LEAD_STATE = {
  name: "",
  phone: "",
  email: "",
  status: "",
  scheduled_time: "",
  context: "",
  address: "",
  project_description: "",
  proposed_price: "",
  proposal_date: "",
  project_type: "",
  notes: "",
};

/* Helpers */
const formatDate = (s) => (s ? new Date(s).toLocaleDateString() : "Never");

const formatStatusLabel = (status) =>
  status
    ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Active";

const getStatusVariant = (status) => {
  if (!status) return "secondary";
  switch (status) {
    case "ready_to_schedule":
    case "proposal_sent":
      return "primary";
    case "call_later":
    case "follow_up_required":
    case "pending_decision":
      return "warning";
    case "cancelled":
    case "chose_different_company":
      return "danger";
    default:
      return "secondary";
  }
};

/* ===========================================================
 * DESKTOP LEAD ROW (click to expand)
 * =========================================================== */
const LeadRow = ({
  lead,
  selectedIds,
  toggleSelect,
  handleCall,
  handleEditLead,
  openDeleteModal,
  isActionLoading,
  expanded,
  onToggleExpand,
  handleRequestReview,
  handleMoveToSeasonal,
  setLeadToMove,
  setShowMoveSeasonalModal,
}) => {
  const status = lead.status || "None";

  return (
    <>
      {/* MAIN ROW */}
      <tr
        className={`lead-row-clickable ${expanded ? "table-active" : ""}`}
        onClick={() => onToggleExpand(lead)}
        style={{ cursor: "pointer" }}
      >
        <td onClick={(e) => e.stopPropagation()}>
          <Form.Check
            type="checkbox"
            checked={selectedIds.includes(lead.id)}
            onChange={() => toggleSelect(lead.id)}
          />
        </td>

        <td>
          <div className="d-flex align-items-center">
            <div className="lead-avatar">
              {lead.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="ms-2">
              <div className="fw-semibold">{lead.name}</div>
              {lead.email && <small className="text-muted">{lead.email}</small>}
            </div>
          </div>
        </td>

        <td className="mono">{lead.phone}</td>
        <td>{formatDate(lead.last_contacted)}</td>

        <td>
          <Badge bg={getStatusVariant(status)}>{formatStatusLabel(status)}</Badge>
        </td>

        {/* ACTIONS */}
        <td onClick={(e) => e.stopPropagation()} className="lead-actions-cell">
          <div className="lead-actions">
            <button
              className="lead-action-btn call"
              onClick={(e) => {
                e.stopPropagation();
                handleCall(lead.id);
              }}
            >
              <FaPhone />
            </button>

            <button
              className="lead-action-btn edit"
              onClick={(e) => {
                e.stopPropagation();
                handleEditLead(lead);
              }}
            >
              <FaEdit />
            </button>

            <button
              className="lead-action-btn move"
              title="Move to seasonal list"
              disabled={isActionLoading}
              onClick={(e) => {
                e.stopPropagation();
                setLeadToMove(lead);
                setShowMoveSeasonalModal(true);
              }}
            >
              {isActionLoading ? <FaSpinner className="spin" /> : <FaArrowRight />}
            </button>

            <button
              className="lead-action-btn delete"
              disabled={isActionLoading}
              onClick={(e) => {
                e.stopPropagation();
                openDeleteModal(lead);
              }}
            >
              <FaTrash />
            </button>

            <button
              className="lead-action-btn review"
              title="Request Review"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestReview(lead);
              }}
            >
              <FaStar />
            </button>
          </div>
        </td>
      </tr>

      {/* EXPANDED DETAILS ROW */}
      {expanded && (
        <tr className="expanded-row">
          <td colSpan={6}>
            <Card className="shadow-sm my-2">
              <Card.Body>
                <Row className="mb-3">
                  <Col md={6}>
                    <small className="text-muted">Address</small>
                    <div>{lead.address || "Not provided"}</div>
                  </Col>

                  <Col md={3}>
                    <small className="text-muted">Proposed Price</small>
                    <div>{lead.proposed_price || "Not set"}</div>
                  </Col>

                  <Col md={3}>
                    <small className="text-muted">Proposal Date</small>
                    <div>
                      {lead.proposal_date
                        ? formatDate(lead.proposal_date)
                        : "Not set"}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <small className="text-muted">Project Description</small>
                    <div>{lead.project_description || "No project description."}</div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <small className="text-muted">Lead Context</small>
                    <div>{lead.context || "No context provided."}</div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </td>
        </tr>
      )}
    </>
  );
};

/* ===========================================================
 * MAIN PAGE
 * =========================================================== */
const LeadManagement = () => {
  const navigate = useNavigate();
  const { activeCall } = useAppContext();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Modals
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [showExtractPreviewModal, setShowExtractPreviewModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showMoveSeasonalModal, setShowMoveSeasonalModal] = useState(false);
  const [leadToMove, setLeadToMove] = useState(null);

  const [formErrors, setFormErrors] = useState({});
  const [newLead, setNewLead] = useState(DEFAULT_LEAD_STATE);
  const [selectedLead, setSelectedLead] = useState(null);
  const [reviewLead, setReviewLead] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [consentGiven, setConsentGiven] = useState(false);
  const [selectedProposalsForReview, setSelectedProposalsForReview] = useState([]);
  const [reviewData, setReviewData] = useState({}); // pk => { mode: 'manual'|'upload', text: '', file: null }
  const [fileInputKeys, setFileInputKeys] = useState({});
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadedFileNames, setUploadedFileNames] = useState([]);
  const [extractedPreview, setExtractedPreview] = useState(null);


  const [showBatchPreview, setShowBatchPreview] = useState(false);
  const [batchLeads, setBatchLeads] = useState([]);
  const [batchCreating, setBatchCreating] = useState(false);
  const [batchName, setBatchName] = useState("");
  const [previewSearch, setPreviewSearch] = useState("");

  // Category-based batch call states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryLeads, setCategoryLeads] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [showCategoryLeadSelection, setShowCategoryLeadSelection] = useState(false);
  const [selectedCategoryLeads, setSelectedCategoryLeads] = useState([]);

  const [scheduledTime, setScheduledTime] = useState("");

  const normalizedAppliedSearch = appliedSearch.trim();

const PREVIEW_PAGE_SIZE = 20;
  const [previewPage, setPreviewPage] = useState(1);
// Reset preview pagination when modal opens

  /* DATA FETCH */
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [...LEADS_QUERY_KEY, page, statusFilter, normalizedAppliedSearch],
    queryFn: () =>
      fetchLeads({
        page,
        status: statusFilter === "all" ? undefined : statusFilter,
        search: normalizedAppliedSearch || undefined,
      }),
    keepPreviousData: true,
  });

  const leads = useMemo(() => data?.results || [], [data]);

  const totalPages = useMemo(() => data?.count ? Math.ceil(data.count / PAGE_SIZE) : 1, [data?.count]);

  const totalCount = data?.count || 0;
  const visibleStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const visibleEnd = totalCount === 0 ? 0 : Math.min(page * PAGE_SIZE, totalCount);
  const pageWindowStart = Math.max(1, Math.min(page - 2, totalPages - 4));
  const visiblePageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => pageWindowStart + i);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, normalizedAppliedSearch]);

  useEffect(() => {
  if (showBatchPreview) {
    setPreviewPage(1);
  }
}, [showBatchPreview]);

  useEffect(() => {
    if (showReviewModal && reviewLead) {
      const fetchProposals = async () => {
        try {
          const res = await requestReviewAPI.getClientProposalsForReview(reviewLead.id);
          setProposals(res.data.proposals || []);
        } catch (error) {
          toast.error("Failed to fetch proposals for review.");
          console.error(error);
        }
      };
      fetchProposals();
    }
  }, [showReviewModal, reviewLead]);

// -------- Batch preview filtering (with search) --------
  const selectedLeads = useMemo(() => {
    const allLeads = categoryLeads.length > 0 ? categoryLeads : leads;
    return allLeads.filter(lead => batchLeads.includes(lead.id)) || [];
  }, [categoryLeads, leads, batchLeads]);

  const previewLeads = useMemo(() => {
    const q = previewSearch.toLowerCase();
    return selectedLeads.filter(
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        l.phone?.includes(q) ||
        (l.email || "").toLowerCase().includes(q)
    );
  }, [selectedLeads, previewSearch]);

const statusFilteredPreviewLeads = useMemo(() => {
  return previewLeads; // No additional status filter since already category-filtered
}, [previewLeads]);

const totalPreviewPages = Math.max(
  1,
  Math.ceil(statusFilteredPreviewLeads.length / PREVIEW_PAGE_SIZE)
);

const paginatedPreviewLeads = useMemo(() => {
  return statusFilteredPreviewLeads.slice(
    (previewPage - 1) * PREVIEW_PAGE_SIZE,
    previewPage * PREVIEW_PAGE_SIZE
  );
}, [statusFilteredPreviewLeads, previewPage]);


  /* Leads already filtered by backend search/status */
  const filtered = useMemo(() => {
    return leads;
  }, [leads]);

  /* SELECT ROW (EXPAND PANEL) */
  const toggleExpand = (lead) =>
    setExpandedLeadId((prev) => (prev === lead.id ? null : lead.id));

  /* HANDLE CATEGORY SELECTION FOR BATCH CALL */
  const handleCategorySelect = async (category) => {
    setSelectedCategory(category);
    setCategoryLoading(true);
    try {
      const data = await fetchLeadsByCategory({ status: category.toLowerCase() });
      setCategoryLeads(data.results || []);
      setShowCategoryModal(false);
      setShowCategoryLeadSelection(true);
    } catch (error) {
      toast.error("Failed to fetch leads for this category.");
      console.error(error);
    } finally {
      setCategoryLoading(false);
    }
  };

  /* TOGGLE SELECT CATEGORY LEAD */
  const toggleCategoryLeadSelect = (id) => {
    setSelectedCategoryLeads((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* SUBMIT CATEGORY LEADS FOR BATCH CALL */
  const handleSubmitCategoryBatch = () => {
    if (!selectedCategoryLeads.length) {
      toast.error("Please select at least one lead.");
      return;
    }
    setBatchLeads(selectedCategoryLeads);
    setShowCategoryLeadSelection(false);
    setShowBatchPreview(true);
  };

  /* VALIDATION */
  const validateLead = (lead) => {
    const errors = {};
    if (!lead.name?.trim()) errors.name = "Name is required";
    if (!lead.phone || !/^\+\d+$/.test(lead.phone)) {
      errors.phone = "Phone must start with + and contain digits only";
    }
    if (!lead.project_description?.trim()) {
      errors.project_description = "Project description is required";
    }
    return errors;
  };

  useEffect(() => {
  setPreviewPage(1);
}, [previewSearch]);


  // Helper to normalize backend errors (arrays or strings)
  const getError = (field) => {
    const val = formErrors[field];
    if (!val) return null;
    if (Array.isArray(val)) return val.join(" ");
    return String(val);
  };

  /* EDIT LEAD CLICK */
  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setFormErrors({});
    setShowEdit(true);
  };

  /* DELETE MODAL OPEN */
  const openDeleteModal = (lead) => {
    setSelectedLead(lead);
    setShowDelete(true);
  };
/* UPDATE LEAD (Edit Modal) */
const handleUpdateLead = async (e) => {
  e.preventDefault();

  const errors = validateLead(selectedLead);
  if (Object.keys(errors).length) {
    setFormErrors(errors);
    return;
  }

  try {
    setIsActionLoading(true);

    await leadsAPI.updateLeadStatus(selectedLead.id, selectedLead);

    toast.success("Lead updated!");

    setShowEdit(false);
    setSelectedLead(null);
    refetch();
  } catch (err) {
    console.error(err);

    // Backend validation errors
    if (err.response?.data) {
      setFormErrors(err.response.data);
    }

    toast.error("Failed to update lead.");
  } finally {
    setIsActionLoading(false);
  }
};

  /* HANDLE DOCUMENT UPLOAD TO EXTRACT LEAD */
  const handleUploadLeadDocument = async (files) => {
    if (!files.length) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    setUploading(true);
    setUploadedFileNames(files.map((f) => f.name));

    try {
      const res = await leadsAPI.uploadDocuments(formData);
      setExtractedPreview(res.data);
      setShowExtractPreviewModal(true);
      toast.success("Document processed successfully!");
    } catch (err) {
      toast.error("Failed to extract lead from document.");
    } finally {
      setUploading(false);
    }
  };

  /* ADD LEAD */
  const handleAddLead = async (e) => {
    e.preventDefault();

    const errors = validateLead(newLead);
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    try {
      setIsActionLoading(true);
      await leadsAPI.createLead(cleanPayload(newLead));
      toast.success("Lead added!");
      setShowAdd(false);
      setNewLead(DEFAULT_LEAD_STATE);
      setPage(1);
      refetch();
    } catch (error) {
      if (error.response?.data) {
        setFormErrors(error.response.data);
      }
      toast.error("Failed to add lead.");
    } finally {
      setIsActionLoading(false);
    }
  };

  /* CALL LEAD */
  const handleCall = (leadId) => {
    if (activeCall) {
      toast.warning("Call already in progress");
      return navigate(`/live-call/${activeCall.lead.id}`);
    }
    navigate(`/live-call/${leadId}`);
  };

  /* REQUEST REVIEW */
  const handleRequestReview = (lead) => {
    setReviewLead(lead);
    setShowReviewModal(true);
  };

  const handleInitiateReview = async () => {
    if (!selectedProposalsForReview.length) {
      toast.error("Please select at least one proposal to request review.");
      return;
    }
    if (!consentGiven) {
      toast.error("Customer consent is required to initiate the review call.");
      return;
    }
    try {
      setIsActionLoading(true);

      // Check that each selected has data
      for (const pk of selectedProposalsForReview) {
        const data = reviewData[pk] || { mode: 'manual', text: '', file: null };
        if (!data.text.trim() && !data.file) {
          const proposal = proposals.find(p => p.pk === pk);
          toast.error(`Please provide job closeout details for proposal ${proposal.proposal_id}.`);
          return;
        }
      }
      for (const pk of selectedProposalsForReview) {
        const data = reviewData[pk] || { mode: 'manual', text: '', file: null };
        // const proposal = proposals.find(p => p.pk === pk);
        const formData = new FormData();
        formData.append('proposal_pk', pk);
        if (data.mode === 'manual') {
          if (data.text.trim()) {
            formData.append('text', data.text);
          }
        } else if (data.file) {
          formData.append('file', data.file);
        }
        if (data.text.trim() || data.file) {
          await requestReviewAPI.uploadJobCloseoutSheet(formData);
        }
      }

      // Then, initiate the call
      const payload = {
        proposal_ids: selectedProposalsForReview.join(','),
        consent: true,
      };
      await requestReviewAPI.initiateReviewCall(payload);

      toast.success("Review call(s) initiated successfully!");
      setShowReviewModal(false);
      setSelectedProposalsForReview([]);
      setReviewData({});
      setConsentGiven(false);
      refetch(); // Refresh leads if needed
    } catch (error) {
      toast.error("Failed to initiate review call.");
      console.error(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  /* MOVE TO SEASONAL */
  const handleMoveToSeasonal = useCallback(async (lead) => {
    if (!lead) return;
    setIsActionLoading(true);
    try {
      await leadsAPI.moveToSeasonal({ lead_id: lead.id, update_if_exists: true, delete_original: true });
      toast.success('Lead moved to seasonal list');
      refetch();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Failed to move lead');
    } finally {
      setIsActionLoading(false);
    }
  }, [refetch]);


  /* SELECT CHECKBOX */
  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  /* BULK DELETE */
  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    try {
      setIsActionLoading(true);
      await leadsAPI.bulkDeleteLeads(selectedIds);
      toast.success(`${selectedIds.length} lead(s) deleted`);
      setSelectedIds([]);
      setShowBulkDelete(false);
      refetch();
    } catch (err) {
      toast.error("Failed to delete selected leads.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleBulkMoveToSeasonal = async () => {
    if (!selectedIds.length) return;
    try {
      setIsActionLoading(true);
      let successCount = 0;
      let failureCount = 0;

      for (const leadId of selectedIds) {
        try {
          await leadsAPI.moveToSeasonal({ lead_id: leadId });
          successCount++;
        } catch (err) {
          failureCount++;
          console.error(`Failed to move lead ${leadId}:`, err);
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} lead(s) moved to seasonal list`);
      }
      if (failureCount > 0) {
        toast.warning(`${failureCount} lead(s) failed to move`);
      }

      setSelectedIds([]);
      setShowBulkMoveModal(false);
      refetch();
    } catch (err) {
      toast.error("Failed to move leads to seasonal.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setCsvLoading(true);
    try {
      const response = await leadsAPI.exportLeadsCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_proposals.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Leads and proposals exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export leads and proposals');
    } finally {
      setCsvLoading(false);
    }
  };

  /* generate once */
  useEffect(() => {
    const ts = new Date()
      .toISOString()
      .replace("T", " ")
      .replace("Z", "");

    setBatchName(`Batch-${ts}`);
  }, []);

  /* hidden input (no UI) */
  <input type="hidden" value={batchName} />


  /* CSV UPLOAD HANDLER */
  const handleCSVUpload = async (rows) => {
    if (!rows || !rows.length) {
      toast.error("No rows to import.");
      return;
    }
    try {
      setCsvLoading(true);
      await leadsAPI.bulkCreateLeads(rows);
      toast.success(`Imported ${rows.length} lead(s) successfully!`);
      setShowCSV(false);
      setPage(1);
      refetch();
    } catch (error) {
      toast.error("Failed to import leads.");
    } finally {
      setCsvLoading(false);
    }
  };


  useEffect(() => {
    if (showBatchPreview) {
      setPreviewSearch("");
      setPreviewPage(1);
    }
  }, [showBatchPreview]);

  const handleCreateBatchCall = async () => {
  if (!batchName.trim()) {
    toast.error("Batch name is required");
    return;
  }

  if (scheduledTime && new Date(scheduledTime) < new Date()) {
    toast.error("Scheduled time must be in the future");
    return;
  }

  try {
    setBatchCreating(true);

    const payload = {
      name: batchName.trim(),
      lead_ids: batchLeads,
    };

    if (scheduledTime) {
      payload.scheduled_time = new Date(scheduledTime).toISOString();
    }

    await batchCallsAPI.createBatchCall(payload);

    toast.success(
      scheduledTime
        ? "Batch call scheduled successfully"
        : `Batch started with ${batchLeads.length} leads`
    );

    // Reset + close
    setShowBatchPreview(false);
    setBatchLeads([]);
    setBatchName("");
    setScheduledTime("");
    setSelectedCategory("");
    setCategoryLeads([]);
    setSelectedCategoryLeads([]);
  } catch (err) {
    toast.error(
      err.response?.data?.error || "Failed to create batch call"
    );
  } finally {
    setBatchCreating(false);
  }
};


  const categoryCounts = useMemo(() => {
    const counts = {
      all: 0,
      Active: 0,
      Scheduled: 0,
      "Ready for Proposal": 0,
      "Immediate Follow-Up": 0,
      "On Hold": 0,
      Cancelled: 0,
      Others: 0,
    };

    leads.forEach((lead) => {
      counts.all += 1;
      const status = lead.status || "Active";
      const statusKey = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      if (counts[statusKey] !== undefined) {
        counts[statusKey] += 1;
      } else {
        counts.Others += 1; // Any status not in the list goes to Others
      }
    });

    return counts;
  }, [leads]);


  // /* DOWNLOAD SAMPLE CSV */
  // const handleDownloadSampleCSV = () => {
  //   const headers = [
  //     "name",
  //     "phone",
  //     "email",
  //     "address",
  //     "project_description",
  //     "context",
  //     "proposed_price",
  //     "proposal_date",
  //     "project_type",
  //     "notes",
  //   ];

  //   const sampleRow = [
  //     "John Doe",
  //     "+1234567890",
  //     "john@example.com",
  //     "123 Main St",
  //     "Interior painting for 3 rooms",
  //     "Came from website form",
  //     "2500",
  //     "2025-01-15",
  //     "Residential painting",
  //     "Follow up next week",
  //   ];

  //   const csvContent = [
  //     headers.join(","),
  //     sampleRow.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","),
  //   ].join("\n");

  //   const blob = new Blob([csvContent], {
  //     type: "text/csv;charset=utf-8;",
  //   });
  //   const url = URL.createObjectURL(blob);
  //   const link = document.createElement("a");
  //   link.href = url;
  //   link.setAttribute("download", "leads_sample.csv");
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  //   URL.revokeObjectURL(url);
  // };

  /* RENDER LOGIC */
  if (isLoading) return <LoadingSpinner text="Loading leads..." />;

  if (isError)
    return (
      <div className="text-center mt-5">
        <p className="text-danger">Error loading leads</p>
        <Button onClick={refetch}>Retry</Button>
      </div>
    );

  return (
    <Container fluid className="p-4">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div className="flex-grow-1">
          <h1 className="fw-bold">Proposal Leads {data?.count ? `(${data.count})` : ""}</h1>
          <div className="text-muted">
            Track and manage submitted proposals. Reach out to leads to assess interest, automatically schedule follow-up calls based on conversation outcomes, and notify qualified prospects via SMS and email.
          </div>
        </div>

        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setShowInstructions(true)}
          className="d-flex align-items-center ms-3"
        >
          <FaQuestionCircle className="me-2" />
          Instructions
        </Button>
      </div>

        {/* ===== ACTION BUTTONS (MOBILE-FIRST) ===== */}
<div className="mb-4">

  {/* PRIMARY ACTION */}
  <Button
    variant="primary"
    className="w-100 mb-3 fw-semibold"
    size="lg"
    onClick={() => setShowCategoryModal(true)}
  >
    <FaPhone className="me-2" />
    Category Call
  </Button>

  {/* SECONDARY ACTIONS */}
  <Row className="g-2 mb-3">
    <Col xs={4}>
      <Button
        variant="outline-primary"
        className="w-100"
        onClick={() => setShowCSV(true)}
      >
        <FaUpload className="me-2" />
        Import CSV
      </Button>
    </Col>

    <Col xs={4}>
      <Button
        variant="outline-success"
        className="w-100"
        onClick={handleExportCSV}
        disabled={csvLoading}
      >
        <FaDownload className="me-2" />
        {csvLoading ? 'Exporting...' : 'Export Proposals'}
      </Button>
    </Col>

    <Col xs={4}>
      <Button
        variant="outline-primary"
        className="w-100"
        onClick={() => setShowAdd(true)}
      >
        <FaPlus className="me-2" />
        Add Lead
      </Button>
    </Col>
  </Row>

  {/* UPLOAD DOCUMENT */}
  <label className="upload-lead-btn w-100">
    <div className="upload-lead-content justify-content-center">
      <FaUpload className="me-2" />
      <span className="fw-semibold">Upload Proposal to Auto-Fill</span>
    </div>

    <input
      type="file"
      hidden
      multiple
      accept=".pdf,.doc,.docx"
      onChange={(e) => {
        const files = Array.from(e.target.files || []);
        handleUploadLeadDocument(files);
        e.target.value = "";
      }}
    />
  </label>

</div>






      {/* SEARCH & BULK ACTIONS */}
      <Row className="mb-4 align-items-end">
        <Col md={5}>
          <Form.Label>
            <FaSearch className="me-2" /> Search Leads
          </Form.Label>
          <Form.Control
            placeholder="Search by name or phone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Status</Form.Label>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="Active">Active</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Cancelled">Cancelled</option>
          </Form.Select>
        </Col>
        <Col md={1}>
          <Button
            className="w-100 mt-4"
            onClick={() => setAppliedSearch(searchInput.trim())}
            disabled={searchInput.trim() === appliedSearch.trim()}
          >
            Search
          </Button>
        </Col>
        <Col md={2}>
          <Button
            className="w-100 mt-4"
            disabled={!searchInput && !appliedSearch}
            onClick={() => {
              setSearchInput("");
              setAppliedSearch("");
            }}
          >
            Reset
          </Button>
        </Col>
        <Col md={2} className="text-end mt-4">
          {selectedIds.length > 0 && (
            <div className="d-flex gap-2 justify-content-end">
              <Button
                style={{ background: "linear-gradient(135deg, #8b5cf6, #7c3aed)", border: "none" }}
                onClick={() => setShowBulkMoveModal(true)}
                disabled={isActionLoading}
              >
                <FaArrowRight className="me-2" />
                Move ({selectedIds.length})
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowBulkDelete(true)}
                disabled={isActionLoading}
              >
                <FaTrash className="me-2" />
                Delete ({selectedIds.length})
              </Button>
            </div>
          )}
        </Col>
      </Row>

      {/* DESKTOP TABLE (md and up) */}
      <div className="d-none d-md-block table-responsive">
        <Table hover>
          <thead>
            <tr>
              <th>
                <Form.Check
                  type="checkbox"
                  checked={
                    filtered.length > 0 &&
                    filtered.every((l) => selectedIds.includes(l.id))
                  }
                  onChange={() => {
                    const ids = filtered.map((l) => l.id);
                    const allSelected = ids.every((id) =>
                      selectedIds.includes(id)
                    );
                    if (allSelected) {
                      setSelectedIds((prev) =>
                        prev.filter((id) => !ids.includes(id))
                      );
                    } else {
                      setSelectedIds((prev) => [
                        ...new Set([...prev, ...ids]),
                      ]);
                    }
                  }}
                />
              </th>
              <th>Name</th>
              <th>Phone</th>
              <th>Last Contacted</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length ? (
              filtered.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  selectedIds={selectedIds}
                  toggleSelect={toggleSelect}
                  handleCall={handleCall}
                  handleEditLead={handleEditLead}
                  openDeleteModal={openDeleteModal}
                  isActionLoading={isActionLoading}
                  expanded={expandedLeadId === lead.id}
                  onToggleExpand={toggleExpand}
                  handleRequestReview={handleRequestReview}
                  handleMoveToSeasonal={handleMoveToSeasonal}
                  setLeadToMove={setLeadToMove}
                  setShowMoveSeasonalModal={setShowMoveSeasonalModal}
                />
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  <FaFilter size={22} className="mb-2" />
                  <div>No leads found</div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* MOBILE CARD LIST (below md) */}
      <div className="lead-list-mobile d-md-none">
        {filtered.length ? (
          filtered.map((lead) => (
            <div
              key={lead.id}
              className="lead-card-mobile shadow-sm"
              onClick={() => toggleExpand(lead)}
            >
              <div className="lead-card-header">
                <div className="lead-avatar-circle">
                  {lead.name?.charAt(0)?.toUpperCase() || "?"}
                </div>

                <div className="lead-card-info">
                  <h5 className="lead-name">{lead.name}</h5>
                  <p className="lead-phone">
                    <FaPhone /> {lead.phone}
                  </p>
                </div>

                <Badge
                  bg={getStatusVariant(lead.status)}
                  className="lead-status-badge"
                >
                  {formatStatusLabel(lead.status)}
                </Badge>
              </div>

              {/* Expandable details */}
              {expandedLeadId === lead.id && (
                <div className="lead-card-details">
                  <p>
                    <strong>Address:</strong> {lead.address || "Not provided"}
                  </p>
                  <p>
                    <strong>Project:</strong>{" "}
                    {lead.project_description || "—"}
                  </p>
                  <p>
                    <strong>Context:</strong> {lead.context || "—"}
                  </p>

                  <div className="lead-card-actions">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleCall(lead.id)}
                    >
                      <FaPhone /> Call
                    </Button>

                    <Button
                      size="sm"
                      variant="warning"
                      onClick={() => handleEditLead(lead)}
                    >
                      <FaEdit /> Edit
                    </Button>

                    <Button
                      size="sm"
                      style={{ background: "linear-gradient(135deg, #8b5cf6, #7c3aed)", border: "none", color: "white" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLeadToMove(lead);
                        setShowMoveSeasonalModal(true);
                      }}
                      disabled={isActionLoading}
                    >
                      <FaArrowRight /> Move
                    </Button>

                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => openDeleteModal(lead)}
                    >
                      <FaTrash /> Delete
                    </Button>

                    <Button
                      size="sm"
                      variant="info"
                      onClick={() => handleRequestReview(lead)}
                    >
                      <FaStar /> Review
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-4">
            <FaFilter size={22} className="mb-2" />
            <div>No leads found</div>
          </div>
        )}
      </div>

      {/* PAGINATION */}
      <div className="text-center text-muted mb-2">
        Showing {visibleStart}-{visibleEnd} of {totalCount} leads
      </div>
      {totalCount > 0 && (
        <Pagination className="justify-content-center mt-4">
          {data?.previous && (
            <Pagination.Prev onClick={() => setPage((p) => Math.max(1, p - 1))} />
          )}
          {visiblePageNumbers.map((pageNum) => (
            <Pagination.Item
              key={pageNum}
              active={pageNum === page}
              onClick={() => setPage(pageNum)}
            >
              {pageNum}
            </Pagination.Item>
          ))}
          {data?.next && <Pagination.Next onClick={() => setPage((p) => p + 1)} />}
        </Pagination>
      )}

      {/* UPLOAD OVERLAY */}
      {uploading && (
        <div className="loader-overlay">
          <div className="loader-box text-center">
            <FaSpinner className="spin loader-icon" />
            <h4>Processing uploaded documents…</h4>

            {uploadedFileNames.length > 0 && (
              <>
                <p className="loader-subtext">We’re extracting leads from:</p>
                <ul className="loader-file-list">
                  {uploadedFileNames.map((name, idx) => (
                    <li key={idx}>{name}</li>
                  ))}
                </ul>
              </>
            )}

            <p className="loader-subtext">This may take a moment.</p>
          </div>
        </div>
      )}

      {/* ====================== MODALS ====================== */}

      {/* BULK MOVE TO SEASONAL CONFIRMATION MODAL */}
      <Modal centered show={showBulkMoveModal} onHide={() => setShowBulkMoveModal(false)} backdrop="static" size="lg">
        <Modal.Header closeButton style={{ borderBottom: "none", paddingBottom: "0.5rem", padding: "1rem" }}>
          <Modal.Title className="fw-semibold d-flex align-items-center" style={{ fontSize: "clamp(1.1rem, 4vw, 1.5rem)" }}>
            <FaArrowRight className="me-2" style={{ color: "#8b5cf6" }} /> Move to Seasonal
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "clamp(1rem, 5vw, 2rem)" }}>
          <div>
            <div style={{ fontSize: "clamp(1.8rem, 8vw, 2.5rem)", marginBottom: "1rem", color: "#8b5cf6", textAlign: "center" }}>
              <FaArrowRight />
            </div>
            <h5 className="fw-bold mb-3 text-center" style={{ fontSize: "clamp(1rem, 4vw, 1.25rem)" }}>
              Move {selectedIds.length} Lead{selectedIds.length > 1 ? "s" : ""}
            </h5>
            <p className="text-muted mb-3 text-center" style={{ fontSize: "clamp(0.9rem, 3.5vw, 1rem)" }}>
              Are you sure you want to move all selected leads to the seasonal list?
            </p>
            
            <Alert variant="warning" className="mb-3" style={{ fontSize: "clamp(0.85rem, 3vw, 0.95rem)" }}>
              <div className="fw-semibold mb-2">The following will happen for each lead:</div>
              <ul className="mb-0 ps-3">
                <li><strong>Lead Information:</strong> All lead details will be moved to seasonal list</li>
                <li><strong>Proposal PDFs:</strong> Associated proposal documents will be deleted</li>
                <li><strong>Call History:</strong> All call records will be archived with the lead</li>
                <li><strong>Contact Data:</strong> Phone, email, and address will be preserved</li>
              </ul>
            </Alert>

            <Alert variant="danger" style={{ fontSize: "clamp(0.85rem, 3vw, 0.95rem)" }}>
              <FaExclamationTriangle className="me-2" size={14} />
              <strong>This action cannot be undone.</strong> Please review before confirming.
            </Alert>
          </div>
        </Modal.Body>

        <Modal.Footer className="py-3" style={{ padding: "clamp(0.75rem, 3vw, 1rem)" }}>
          <Button
            variant="secondary"
            onClick={() => setShowBulkMoveModal(false)}
            disabled={isActionLoading}
            size="sm"
            style={{ fontSize: "clamp(0.85rem, 2.5vw, 1rem)" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            style={{ background: "linear-gradient(135deg, #8b5cf6, #7c3aed)", border: "none", fontSize: "clamp(0.85rem, 2.5vw, 1rem)" }}
            onClick={handleBulkMoveToSeasonal}
            disabled={isActionLoading}
            size="sm"
          >
            {isActionLoading ? <><FaSpinner className="spin me-2" /> Moving...</> : <><FaArrowRight className="me-2" /> Move All</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MOVE TO SEASONAL CONFIRMATION MODAL */}
      <Modal centered show={showMoveSeasonalModal} onHide={() => setShowMoveSeasonalModal(false)} backdrop="static" size="lg">
        <Modal.Header closeButton style={{ borderBottom: "none", paddingBottom: "0.5rem", padding: "1rem" }}>
          <Modal.Title className="fw-semibold d-flex align-items-center" style={{ fontSize: "clamp(1.1rem, 4vw, 1.5rem)" }}>
            <FaArrowRight className="me-2" style={{ color: "#8b5cf6" }} /> Move to Seasonal
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "clamp(1rem, 5vw, 2rem)" }}>
          <div>
            <div style={{ fontSize: "clamp(1.8rem, 8vw, 2.5rem)", marginBottom: "1rem", color: "#8b5cf6", textAlign: "center" }}>
              <FaArrowRight />
            </div>
            <h5 className="fw-bold mb-3 text-center" style={{ fontSize: "clamp(1rem, 4vw, 1.25rem)" }}>
              {leadToMove?.name || leadToMove?.phone}
            </h5>
            <p className="text-muted mb-3 text-center" style={{ fontSize: "clamp(0.9rem, 3.5vw, 1rem)" }}>
              Are you sure you want to move this lead to the seasonal list?
            </p>
            
            <Alert variant="warning" className="mb-3" style={{ fontSize: "clamp(0.85rem, 3vw, 0.95rem)" }}>
              <div className="fw-semibold mb-2">The following will happen:</div>
              <ul className="mb-0 ps-3">
                <li><strong>Lead Information:</strong> All lead details will be moved to seasonal list</li>
                <li><strong>Proposal PDFs:</strong> Associated proposal documents will be deleted</li>
                <li><strong>Call History:</strong> All call records will be archived with the lead</li>
                <li><strong>Contact Data:</strong> Phone, email, and address will be preserved</li>
              </ul>
            </Alert>

            <Alert variant="danger" style={{ fontSize: "clamp(0.85rem, 3vw, 0.95rem)" }}>
              <FaExclamationTriangle className="me-2" size={14} />
              <strong>This action cannot be undone.</strong> Please review before confirming.
            </Alert>
          </div>
        </Modal.Body>

        <Modal.Footer className="py-3" style={{ padding: "clamp(0.75rem, 3vw, 1rem)" }}>
          <Button
            variant="secondary"
            onClick={() => setShowMoveSeasonalModal(false)}
            disabled={isActionLoading}
            size="sm"
            style={{ fontSize: "clamp(0.85rem, 2.5vw, 1rem)" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            style={{ background: "linear-gradient(135deg, #8b5cf6, #7c3aed)", border: "none", fontSize: "clamp(0.85rem, 2.5vw, 1rem)" }}
            onClick={() => {
              if (leadToMove) {
                handleMoveToSeasonal(leadToMove);
                setShowMoveSeasonalModal(false);
                setLeadToMove(null);
              }
            }}
            disabled={isActionLoading}
            size="sm"
          >
            {isActionLoading ? <><FaSpinner className="spin me-2" /> Moving...</> : <><FaArrowRight className="me-2" /> Move to Seasonal</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ADD LEAD MODAL */}
      <Modal centered show={showAdd} onHide={() => setShowAdd(false)}>
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
            color: "white",
            borderBottom: "none",
          }}
        >
          <Modal.Title className="fw-semibold d-flex align-items-center">
            <FaPlus className="me-2" /> Add Lead
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "2rem" }}>
          <Form onSubmit={handleAddLead}>
            {/* Lead Info */}
            <h5 className="fw-bold mb-3" style={{ color: "#1e293b" }}>
              <FaUser className="me-2" />
              Lead Information
            </h5>

            <Row className="g-3">
              {/* NAME */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={newLead.name}
                    onChange={(e) =>
                      setNewLead((p) => ({ ...p, name: e.target.value }))
                    }
                    isInvalid={!!getError("name")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("name")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* PHONE */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Phone <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={newLead.phone}
                    onChange={(e) =>
                      setNewLead((p) => ({ ...p, phone: e.target.value }))
                    }
                    isInvalid={!!getError("phone")}
                    placeholder="+1234567890"
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("phone")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* EMAIL */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Email</Form.Label>
                  <Form.Control
                    value={newLead.email}
                    onChange={(e) =>
                      setNewLead((p) => ({ ...p, email: e.target.value }))
                    }
                    isInvalid={!!getError("email")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("email")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* ADDRESS */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Address</Form.Label>
                  <Form.Control
                    value={newLead.address}
                    onChange={(e) =>
                      setNewLead((p) => ({ ...p, address: e.target.value }))
                    }
                    isInvalid={!!getError("address")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("address")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            

            {/* Project Details */}
            <h5 className="fw-bold mt-4 mb-3" style={{ color: "#1e293b" }}>
              <FaTools className="me-2" />
              Project Details
            </h5>

            <Row className="g-3">
              {/* PROJECT DESCRIPTION */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Project Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newLead.project_description}
                    onChange={(e) =>
                      setNewLead((p) => ({
                        ...p,
                        project_description: e.target.value,
                      }))
                    }
                    isInvalid={!!getError("project_description")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("project_description")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* CONTEXT */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Lead Context</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={newLead.context}
                    onChange={(e) =>
                      setNewLead((p) => ({ ...p, context: e.target.value }))
                    }
                    isInvalid={!!getError("context")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("context")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* PRICE */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Proposed Price</Form.Label>
                  <Form.Control
                    value={newLead.proposed_price}
                    onChange={(e) =>
                      setNewLead((p) => ({
                        ...p,
                        proposed_price: e.target.value,
                      }))
                    }
                    isInvalid={!!getError("proposed_price")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("proposed_price")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* PROPOSAL DATE */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Proposal Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newLead.proposal_date}
                    onChange={(e) =>
                      setNewLead((p) => ({
                        ...p,
                        proposal_date: e.target.value,
                      }))
                    }
                    isInvalid={!!getError("proposal_date")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("proposal_date")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* PROJECT TYPE */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Project Type</Form.Label>
                  <Form.Control
                    value={newLead.project_type}
                    onChange={(e) =>
                      setNewLead((p) => ({
                        ...p,
                        project_type: e.target.value,
                      }))
                    }
                    isInvalid={!!getError("project_type")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError("project_type")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Notes */}
            <h5 className="fw-bold mt-4 mb-3" style={{ color: "#1e293b" }}>
              <FaStickyNote className="me-2" />
              Additional Notes
            </h5>
            <Form.Group>
              <Form.Label className="fw-semibold">Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newLead.notes}
                onChange={(e) =>
                  setNewLead((p) => ({ ...p, notes: e.target.value }))
                }
                isInvalid={!!getError("notes")}
              />
              <Form.Control.Feedback type="invalid">
                {getError("notes")}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Footer buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="light"
                onClick={() => {
                  setShowAdd(false);
                  setFormErrors({});
                }}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "0.6rem 1.3rem",
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="success"
                disabled={isActionLoading}
                style={{
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.6rem 1.3rem",
                  fontWeight: 500,
                  color: "white",
                }}
              >
                {isActionLoading ? "Adding..." : "Add Lead"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* DELETE LEAD MODAL */}
      <Modal
        centered
        show={showDelete && !!selectedLead}
        onHide={() => setShowDelete(false)}
        dialogClassName="delete-lead-modal"
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            color: "white",
            borderBottom: "none",
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <FaTrash className="me-2" size={20} /> Delete Lead
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "2rem" }}>
          <div className="text-center">
            <div
              style={{
                width: "70px",
                height: "70px",
                borderRadius: "50%",
                background: "#fee2e2",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "0 auto 1.5rem",
              }}
            >
              <FaTrash size={28} style={{ color: "#dc2626" }} />
            </div>

            <h4 className="fw-bold mb-2" style={{ color: "#1e293b" }}>
              Delete this lead?
            </h4>
            <p style={{ color: "#475569" }}>
              You are about to remove:
              <br />
              <strong>{selectedLead?.name}</strong>
              <br />
              <small className="text-muted">{selectedLead?.phone}</small>
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setShowDelete(false)}
            disabled={isActionLoading}
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            disabled={isActionLoading}
            onClick={async () => {
              if (!selectedLead) return;
              try {
                setIsActionLoading(true);
                await leadsAPI.deleteLead(selectedLead.id);
                toast.success("Lead deleted!");
                setShowDelete(false);
                setSelectedLead(null);
                refetch();
              } catch {
                toast.error("Failed to delete lead.");
              } finally {
                setIsActionLoading(false);
              }
            }}
          >
            {isActionLoading ? "Deleting..." : "Delete Lead"}
          </Button>
        </Modal.Footer>
      </Modal>




{/* EDIT LEAD MODAL */}
<Modal
  centered
  show={showEdit && !!selectedLead}
  onHide={() => setShowEdit(false)}
  dialogClassName="edit-lead-modal-responsive"
  fullscreen="sm-down"
>
  {/* ===== HEADER ===== */}
  <Modal.Header
    closeButton
    className="edit-lead-header"
  >
    <Modal.Title className="fw-semibold d-flex align-items-center">
      <FaEdit className="me-2" />
      Edit Lead
    </Modal.Title>
  </Modal.Header>
{/* ===== BODY ===== */}
<Modal.Body className="edit-lead-body">
  {selectedLead && (
    <Form onSubmit={handleUpdateLead}>
      {/* ===== BASIC INFO ===== */}
      <div className="edit-section">
        <div className="edit-section-title">
          <span className="me-2">
            <FaUser />
          </span>
          Lead Information
        </div>

        <Row className="g-3">
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                autoFocus
                value={selectedLead.name ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => ({ ...p, name: e.target.value }))
                }
                isInvalid={!!getError("name")}
              />
              <Form.Control.Feedback type="invalid">
                {getError("name")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Phone</Form.Label>
              <Form.Control
                value={selectedLead.phone ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => ({ ...p, phone: e.target.value }))
                }
                isInvalid={!!getError("phone")}
              />
              <Form.Control.Feedback type="invalid">
                {getError("phone")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                value={selectedLead.email ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => ({ ...p, email: e.target.value }))
                }
                isInvalid={!!getError("email")}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Address</Form.Label>
              <Form.Control
                value={selectedLead.address ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => ({ ...p, address: e.target.value }))
                }
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* ===== STATUS & SCHEDULE ===== */}
      <div className="edit-section">
        <div className="edit-section-title">Status & Follow-Up</div>

        <Row className="g-3">
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={selectedLead.status ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => {
                    const nextStatus = e.target.value;
                    const needsSchedule =
                      nextStatus === "Scheduled" || nextStatus === "On Hold";

                    return {
                      ...p,
                      status: nextStatus,
                      call_schedule: needsSchedule ? (p.call_schedule ?? "") : "",
                    };
                  })
                }
              >
                <option value="">Select status…</option>
                <option value="Active">Active</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Ready for Proposal">Ready for Proposal</option>
                <option value="Immediate Follow-Up">Immediate Follow-Up</option>
                <option value="On Hold">On Hold</option>
                <option value="Cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {(selectedLead.status === "Scheduled" ||
            selectedLead.status === "On Hold") && (
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Schedule Date & Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={selectedLead.call_schedule ?? ""}
                  onChange={(e) =>
                    setSelectedLead((p) => ({
                      ...p,
                      call_schedule: e.target.value ?? "",
                    }))
                  }
                />
              </Form.Group>
            </Col>
          )}
        </Row>
      </div>

      {/* ===== PROJECT ===== */}
      <div className="edit-section">
        <div className="edit-section-title">
          <span className="me-2">
            <FaTools />
          </span>
          Project Details
        </div>

        <Row className="g-3">
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Project Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={selectedLead.project_description ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => ({
                    ...p,
                    project_description: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Group>
              <Form.Label>Context / Call Script</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={selectedLead.context ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => ({
                    ...p,
                    context: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Proposed Price</Form.Label>
              <Form.Control
                value={selectedLead.proposed_price ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => ({
                    ...p,
                    proposed_price: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>Proposal Date</Form.Label>
              <Form.Control
                type="date"
                value={
                  selectedLead.proposal_date
                    ? selectedLead.proposal_date.substring(0, 10)
                    : ""
                }
                onChange={(e) =>
                  setSelectedLead((p) => ({
                    ...p,
                    proposal_date: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Group>
              <Form.Label>Project Type</Form.Label>
              <Form.Control
                value={selectedLead.project_type ?? ""}
                onChange={(e) =>
                  setSelectedLead((p) => ({
                    ...p,
                    project_type: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* ===== NOTES ===== */}
      <div className="edit-section">
        <div className="edit-section-title">
          <span className="me-2">
            <FaStickyNote />
          </span>
          Notes
        </div>

        <Form.Control
          as="textarea"
          rows={3}
          value={selectedLead.notes ?? ""}
          onChange={(e) =>
            setSelectedLead((p) => ({ ...p, notes: e.target.value }))
          }
        />
      </div>

      {/* ===== FOOTER ===== */}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button variant="outline-secondary" onClick={() => setShowEdit(false)}>
          Cancel
        </Button>

        <Button type="submit" variant="primary" disabled={isActionLoading}>
          {isActionLoading ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </Form>
  )}
</Modal.Body>


</Modal>



      {/* BULK DELETE MODAL */}
      <Modal
        centered
        show={showBulkDelete && selectedIds.length > 0}
        onHide={() => setShowBulkDelete(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FaTrash className="me-2" /> Delete Selected Leads
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete{" "}
            <strong>{selectedIds.length}</strong> selected lead
            {selectedIds.length > 1 ? "s" : ""}? This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setShowBulkDelete(false)}
            disabled={isActionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleBulkDelete}
            disabled={isActionLoading}
          >
            {isActionLoading ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

<Modal
  show={showCSV}
  onHide={() => setShowCSV(false)}
  fullscreen="sm-down"
  centered
  dialogClassName="csv-import-modal"
>
  <Modal.Header closeButton className="csv-modal-header">
    <Modal.Title className="d-flex align-items-center">
      <FaUpload className="me-2" />
      Import Leads
    </Modal.Title>
  </Modal.Header>

  {/* ✅ SCROLL ONLY HERE */}
  <Modal.Body className="csv-modal-body p-0">
    <div className="csv-modal-content">
      <p className="text-muted px-3 pt-3">
        Select the completed CSV file to preview and import leads.
      </p>

      <CSVUploader
        onUpload={handleCSVUpload}
        isLoading={csvLoading}
      />
    </div>
  </Modal.Body>

  {/* ✅ SINGLE STICKY FOOTER */}
  <Modal.Footer className="csv-modal-footer">
    <Button
      variant="outline-secondary"
      onClick={() => setShowCSV(false)}
    >
      Cancel
    </Button>

    <Button
      variant="primary"
      disabled={csvLoading}
    >
      Import Leads
    </Button>
  </Modal.Footer>
</Modal>








{/* EXTRACT PREVIEW MODAL */}
{showExtractPreviewModal && extractedPreview && (
<Modal
  centered
  size="lg"
  fullscreen="sm-down"
  show={showExtractPreviewModal}
  onHide={() => setShowExtractPreviewModal(false)}
  dialogClassName="extract-preview-modal"
  container={document.body}   // ✅ CRITICAL
>


    <Modal.Header closeButton>
      <Modal.Title className="fw-semibold d-flex align-items-center">
        <FaUpload className="me-2 text-primary" />
        Extracted Leads
        <Badge bg="secondary" className="ms-2">
          {extractedPreview.created_leads?.length || 0}
        </Badge>
      </Modal.Title>
    </Modal.Header>

    <Modal.Body
      className="pt-3"
      style={{ maxHeight: "75vh", overflowY: "auto" }}
    >
      {extractedPreview.created_leads?.length > 0 ? (
        extractedPreview.created_leads.map((lead, idx) => (
          <Card
            key={lead.id || idx}
            className="mb-2 border-0 shadow-sm preview-compact-card"
          >
            <Card.Body className="p-3">
              {/* ===== TOP ROW ===== */}
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <div className="preview-avatar-sm">
                    {lead.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>

                  <div>
                    <div className="fw-semibold small mb-0">
                      {lead.name || "Unnamed Lead"}
                    </div>
                    <Badge
                      bg={getStatusVariant(lead.status)}
                      className="preview-status"
                    >
                      {formatStatusLabel(lead.status)}
                    </Badge>
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="outline-primary"
                  className="preview-edit-btn"
                  onClick={() => handleEditLead(lead)}
                >
                  <FaEdit />
                </Button>
              </div>

              {/* ===== INFO GRID ===== */}
              <Row className="gx-3 gy-1 mt-2 small text-muted">
                <Col xs={12} md={6}>
                    <strong>Phone#:</strong> {lead.phone || "—"}
                </Col>

                <Col xs={12} md={6}>
                    <strong>Address:</strong> {lead.address || "—"}
                </Col>

                {lead.proposed_price && (
                  <Col xs={12} md={6}>
                    <strong>Price:</strong> {lead.proposed_price}
                  </Col>


                )}


                {lead.proposal_date && (
                  <Col xs={12} md={6}>
                    <strong>Date:</strong> {lead.proposal_date}
                  </Col>


                )}
              </Row>

              {/* ===== COLLAPSED DETAILS ===== */}
              {(lead.project_description || lead.context) && (
                <div className="preview-details mt-2">
                  {lead.project_description && (
                    <div className="preview-detail-row">
                      <span className="label">Project</span>
                      <span className="value">
                        {lead.project_description}
                      </span>
                    </div>
                  )}

                  {lead.context && (
                    <div className="preview-detail-row">
                      <span className="label">Context</span>
                      <span className="value">{lead.context}</span>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        ))
      ) : (
        <div className="text-center text-muted py-5">
          <FaUpload size={28} className="mb-2" />
          <p className="mb-0">No leads detected in uploaded documents.</p>
        </div>
      )}

      {/* ===== FAILED DOCUMENTS ===== */}
      {extractedPreview.failed_leads?.length > 0 && (
        <>
          <div className="mt-4 fw-semibold text-danger small">
            Failed Documents
          </div>
          {extractedPreview.failed_leads.map((f, i) => (
            <div key={i} className="small text-danger">
              {f.file_name} — {f.reason}
            </div>
          ))}
        </>
      )}
    </Modal.Body>

    <Modal.Footer className="bg-light py-2">
      <Button
        variant="secondary"
        onClick={() => setShowExtractPreviewModal(false)}
      >
        Done
      </Button>
    </Modal.Footer>
  </Modal>
)}







<Modal
  centered
  size="lg"
  fullscreen="sm-down"
  show={showBatchPreview}
  onHide={() => setShowBatchPreview(false)}
  dialogClassName="batch-preview-modal"
>
  {/* ================= HEADER ================= */}
  <Modal.Header closeButton>
    <Modal.Title className="d-flex align-items-center gap-2 fw-semibold">
      <FaPhone />
      Category Call Preview
      <Badge bg="primary">{batchLeads.length}</Badge>
    </Modal.Title>
  </Modal.Header>

  {/* ================= BODY ================= */}
  <Modal.Body className="batch-preview-body">

    {/* ===== FILTER BAR ===== */}
    <Row className="g-2 mb-3">
      <Col md={12}>
        <Form.Control
          placeholder="Search by name, phone, or email..."
          value={previewSearch}
          onChange={(e) => setPreviewSearch(e.target.value)}
        />
      </Col>
    </Row>

    {/* ===== SELECT/DESELECT BUTTONS ===== */}
    <Row className="mb-3">
      <Col>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setBatchLeads(statusFilteredPreviewLeads.map(l => l.id))}
        >
          Select All Filtered
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          className="ms-2"
          onClick={() => setBatchLeads([])}
        >
          Deselect All
        </Button>
      </Col>
    </Row>

    {/* ===== SCHEDULE CARD ===== */}
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">
                Schedule Time <span className="text-muted">(optional)</span>
              </Form.Label>
              <Form.Control
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                disabled={batchCreating}
              />
              <small className="text-muted">
                Leave empty to start immediately
              </small>
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>


    {/* ===== LEADS LIST ===== */}
<p className="text-muted mb-2">
  Showing {((previewPage - 1) * PREVIEW_PAGE_SIZE) + 1}-{Math.min(previewPage * PREVIEW_PAGE_SIZE, statusFilteredPreviewLeads.length)} of {statusFilteredPreviewLeads.length} leads ({batchLeads.length} selected)
</p>


    {paginatedPreviewLeads.length > 0 ? (
      paginatedPreviewLeads.map((lead) => {
        const isSelected = batchLeads.includes(lead.id);

        return (
          <Card
            key={lead.id}
            className={`batch-lead-card ${isSelected ? "selected" : ""}`}
            onClick={() =>
              setBatchLeads((prev) =>
                isSelected
                  ? prev.filter((id) => id !== lead.id)
                  : [...prev, lead.id]
              )
            }
          >
            <Card.Body className="batch-lead-body">
              <div className="batch-lead-info">
                <div className="batch-lead-name">{lead.name}</div>
                <div className="batch-lead-meta">
                  {lead.phone}
                  <Badge
                    bg="secondary"
                    className="ms-2 text-uppercase"
                  >
                    {lead.status || "Active"}
                  </Badge>
                </div>
              </div>

              <Form.Check
                type="checkbox"
                checked={isSelected}
                onClick={(e) => e.stopPropagation()}
                onChange={() => {}}
              />
            </Card.Body>
          </Card>
        );
      })
    ) : (
      <div className="text-center text-muted py-4">
        No leads match your filters.
      </div>
    )}

    {/* ===== PAGINATION ===== */}
    {totalPreviewPages > 1 && (
      <Pagination size="sm" className="justify-content-center mt-3">
        <Pagination.Prev disabled={previewPage === 1} onClick={() => setPreviewPage(p => p - 1)} />
        {Array.from({ length: Math.min(5, totalPreviewPages) }, (_, i) => {
          const pageNum = Math.max(1, previewPage - 2) + i;
          if (pageNum > totalPreviewPages) return null;
          return (
            <Pagination.Item key={pageNum} active={pageNum === previewPage} onClick={() => setPreviewPage(pageNum)}>
              {pageNum}
            </Pagination.Item>
          );
        })}
        <Pagination.Next disabled={previewPage === totalPreviewPages} onClick={() => setPreviewPage(p => p + 1)} />
      </Pagination>
    )}
  </Modal.Body>

  {/* ================= FOOTER ================= */}
  <Modal.Footer className="bg-light border-top d-flex justify-content-between">
    <Button
      variant="outline-secondary"
      disabled={batchCreating}
      onClick={() => {
        setShowBatchPreview(false);
        setScheduledTime("");
        setSelectedCategory("");
        setCategoryLeads([]);
        setSelectedCategoryLeads([]);
      }}
    >
      Cancel
    </Button>

    <Button
      variant="primary"
      disabled={!batchLeads.length || batchCreating}
      onClick={handleCreateBatchCall}
    >
      {batchCreating ? (
        <>
          <FaSpinner className="spin me-2" />
          Creating…
        </>
      ) : scheduledTime ? (
        <>
          <FaClock className="me-2" />
          Schedule Category Call
        </>
      ) : (
        <>
          <FaPlay className="me-2" />
          Start Category Call
        </>
      )}
    </Button>
  </Modal.Footer>
</Modal>

{/* REVIEW REQUEST MODAL */}
<Modal show={showReviewModal} onHide={() => { setShowReviewModal(false); setSelectedProposalsForReview([]); setReviewData({}); setConsentGiven(false); }} size="xl" container={document.body}>
  <Modal.Header closeButton className="py-2">
    <Modal.Title className="d-flex align-items-center h6 mb-0">
      <FaStar className="me-2 text-warning" />
      Request Review for {reviewLead?.name}
    </Modal.Title>
  </Modal.Header>
  <Modal.Body className="p-3">
    {proposals.length === 0 ? (
      <p>No proposals found for this lead.</p>
    ) : (
      <>
        {/* Selected Proposals Summary */}
        {selectedProposalsForReview.length > 0 && (
          <div className="mb-3">
            <h6>Selected Proposals ({selectedProposalsForReview.length})</h6>
            <div className="d-flex flex-wrap gap-2">
              {selectedProposalsForReview.map(pk => {
                const proposal = proposals.find(p => p.pk === pk);
                return (
                  <Badge key={pk} bg="primary" className="d-flex align-items-center gap-1">
                    {proposal.proposal_id}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-1"
                      style={{ fontSize: '0.5em' }}
                      onClick={() => {
                        setSelectedProposalsForReview(prev => prev.filter(id => id !== pk));
                        setReviewData(prev => {
                          const newData = { ...prev };
                          delete newData[pk];
                          return newData;
                        });
                      }}
                    ></button>
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Proposals List */}
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {proposals.map((proposal) => {
            const isSelected = selectedProposalsForReview.includes(proposal.pk);
            const data = reviewData[proposal.pk] || { mode: 'manual', text: '', file: null };
            return (
              <Card 
                key={proposal.pk} 
                className={`mb-2 shadow-sm ${isSelected ? 'border-primary bg-light' : 'border-left-primary'} cursor-pointer`}
                style={{ borderRadius: '8px', cursor: 'pointer' }}
                onClick={() => {
                  if (isSelected) {
                    setSelectedProposalsForReview(prev => prev.filter(id => id !== proposal.pk));
                    setReviewData(prev => {
                      const newData = { ...prev };
                      delete newData[proposal.pk];
                      return newData;
                    });
                  } else {
                    setSelectedProposalsForReview(prev => [...prev, proposal.pk]);
                    setReviewData(prev => ({ ...prev, [proposal.pk]: data }));
                  }
                }}
              >
                <Card.Body className="p-2">
                  {isSelected && <Badge bg="primary" className="mb-2">Selected</Badge>}
                  <Row className="mb-1">
                    <Col md={8}>
                      <div className="d-flex align-items-center mb-1">
                        <FaFileAlt className="me-2 text-primary" size={14} />
                        <strong className="text-primary small">Proposal {proposal.proposal_id}</strong>
                      </div>
                      {proposal.review_requested && (
                        <div className="alert alert-success py-1 px-2 mb-1 small d-flex align-items-center">
                          <FaStar className="me-1 text-warning" size={12} />
                          <span><strong>Requested:</strong> {new Date(proposal.review_call_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="small text-muted mb-1">
                        <FaTools className="me-1" size={12} />
                        {proposal.job_type}
                      </div>
                      <div className="small text-muted mb-1">
                        <FaMapMarkerAlt className="me-1" size={12} />
                        {proposal.address}
                      </div>
                      <div className="small text-muted">
                        <FaCalendarAlt className="me-1" size={12} />
                        Created: {new Date(proposal.created_at).toLocaleDateString()}
                      </div>
                    </Col>
                    <Col md={4} className="text-end d-flex flex-column justify-content-between">
                      <div className="mb-2">
                        <div className="fw-bold text-primary">${proposal.total_price}</div>
                      </div>
                      {proposal.eligible_for_review_call ? (
                        null
                      ) : (
                        <Badge bg="secondary" className="w-100 py-1 small" style={{ borderRadius: '15px' }}>
                          Not Eligible
                        </Badge>
                      )}
                    </Col>
                  </Row>
                  {isSelected && (
                    <Row>
                      <Col>
                        <div onClick={(e) => e.stopPropagation()}>
                        <Tabs activeKey={data.mode} onSelect={(k) => setReviewData(prev => ({ ...prev, [proposal.pk]: { ...data, mode: k } }))}>
                          <Tab eventKey="manual" title="Manual Entry">
                            <Form.Group className="mt-2">
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Enter job closeout details manually..."
                                value={data.text}
                                onChange={(e) => setReviewData(prev => ({ ...prev, [proposal.pk]: { ...data, text: e.target.value } }))}
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontSize: '0.9em' }}
                              />
                            </Form.Group>
                          </Tab>
                          <Tab eventKey="upload" title="Upload Document">
                            <Form.Group className="mt-2">
                              <Form.Control
                                type="file"
                                accept=".pdf,.docx,.txt"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file && file.size > 10 * 1024 * 1024) {
                                    toast.error("File size must be less than 10MB");
                                    return;
                                  }
                                  setReviewData(prev => ({ ...prev, [proposal.pk]: { ...data, file } }));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                key={fileInputKeys[proposal.pk] || 'initial'}
                                style={{ fontSize: '0.9em' }}
                              />
                              {data.file && (
                                <div className="mt-2">
                                  <small className="text-success">Selected: {data.file.name}</small>
                                  <Button variant="link" size="sm" onClick={(e) => { e.stopPropagation(); setReviewData(prev => ({ ...prev, [proposal.pk]: { ...data, file: null } })); setFileInputKeys(prev => ({ ...prev, [proposal.pk]: Date.now() })); }}>
                                    Remove
                                  </Button>
                                </div>
                              )}
                            </Form.Group>
                          </Tab>
                        </Tabs>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </div>

        <Form.Group className="mt-3 p-2 border rounded" style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}>
          <Form.Check
            type="checkbox"
            id="consent-checkbox"
            label={
              <span className="fw-semibold small">
                I confirm that I have obtained customer consent to place an outbound review call. 
                <span className="text-danger">*</span>
              </span>
            }
            checked={consentGiven}
            onChange={(e) => setConsentGiven(e.target.checked)}
            required
            className="consent-checkbox"
            style={{ fontSize: '0.95em' }}
          />
          <Form.Text className="text-muted mt-1 small">
            <FaExclamationTriangle className="me-1 text-warning" size={12} />
            Customer consent is required before initiating any outbound calls.
          </Form.Text>
        </Form.Group>
      </>
    )}
  </Modal.Body>
  <Modal.Footer className="py-2">
    <Button variant="secondary" onClick={() => { setShowReviewModal(false); setSelectedProposalsForReview([]); setReviewData({}); setConsentGiven(false); }} size="sm">
      Close
    </Button>
    <Button 
      variant="primary" 
      onClick={handleInitiateReview}
      disabled={isActionLoading || !selectedProposalsForReview.length || !consentGiven}
      size="sm"
    >
      {isActionLoading ? <><FaSpinner className="spin me-2" /> Requesting...</> : <><FaStar className="me-2" /> Request Review ({selectedProposalsForReview.length})</>}
    </Button>
  </Modal.Footer>
</Modal>

      {/* CATEGORY SELECTION MODAL */}
      <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Category for Batch Call</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Choose a category to fetch leads and initiate a batch call:</p>
          <div className="d-grid gap-2">
            {Object.entries(categoryCounts)
              .filter(([category, count]) => category !== 'all' && count > 0)
              .sort(([,a], [,b]) => b - a) // Sort by count descending
              .map(([category, count]) => (
                <Button
                  key={category}
                  variant="outline-primary"
                  onClick={() => handleCategorySelect(category)}
                  disabled={categoryLoading}
                  className="text-start d-flex justify-content-between align-items-center"
                >
                  <span>{formatStatusLabel(category)}</span>
                  <Badge bg="secondary">{count}</Badge>
                </Button>
              ))}
          </div>
          {categoryLoading && (
            <div className="text-center mt-3">
              <FaSpinner className="spin me-2" />
              Loading leads...
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* CATEGORY LEAD SELECTION MODAL */}
      <Modal show={showCategoryLeadSelection} onHide={() => setShowCategoryLeadSelection(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Leads from {formatStatusLabel(selectedCategory)} Category</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <p>Select the leads you want to include in the batch call:</p>
          {categoryLeads.length === 0 ? (
            <p className="text-muted">No leads found in this category.</p>
          ) : (
            <>
              <Row className="mb-3">
                <Col>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setSelectedCategoryLeads(categoryLeads.map(l => l.id))}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2"
                    onClick={() => setSelectedCategoryLeads([])}
                  >
                    Deselect All
                  </Button>
                </Col>
              </Row>
              <div className="list-group">
                {categoryLeads.map((lead) => (
                  <div key={lead.id} className="list-group-item d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      checked={selectedCategoryLeads.includes(lead.id)}
                      onChange={() => toggleCategoryLeadSelect(lead.id)}
                      className="me-3"
                    />
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{lead.name}</div>
                      <small className="text-muted">{lead.phone} • {lead.email || 'No email'}</small>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCategoryLeadSelection(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitCategoryBatch}
            disabled={selectedCategoryLeads.length === 0}
          >
            Proceed to Batch Call ({selectedCategoryLeads.length})
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Instructions Modal */}
      <Modal
        show={showInstructions}
        onHide={() => setShowInstructions(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FaQuestionCircle className="me-2 text-primary" />
            How to Use Proposal Leads
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="instructions-content">
            <h5 className="text-primary mb-3">📋 Adding Proposal Leads</h5>
            <p>Add proposal leads through three methods:</p>
            <ul>
              <li><strong>Upload Documents:</strong> Upload up to 5 documents at once to extract lead information automatically</li>
              <li><strong>Add Manually:</strong> Enter lead details directly through the form</li>
              <li><strong>Import CSV:</strong> Bulk import leads using a CSV file with predefined columns</li>
            </ul>

            <h5 className="text-primary mb-3 mt-4">🔍 Reviewing Extracted Data</h5>
            <p>After uploading documents, always review the extracted data from documents to ensure accuracy before proceeding.</p>

            <h5 className="text-primary mb-3 mt-4">📞 Managing Individual Leads</h5>
            <ul>
              <li><strong>Initiate Call:</strong> Click the call button to start an immediate conversation with a specific lead</li>
              <li><strong>Edit Lead:</strong> Click the edit button to modify lead information</li>
              <li><strong>Delete Lead:</strong> Click the delete button to remove a lead from the system</li>
            </ul>

            <h5 className="text-primary mb-3 mt-4">⭐ Initiating Review Calls</h5>
            <p>Click the review button on any lead to initiate a review call:</p>
            <ul>
              <li>Select a proposal from the available options</li>
              <li>Add job sheet details and any additional context</li>
              <li>The system will automatically schedule and execute the review call</li>
            </ul>

            <h5 className="text-primary mb-3 mt-4">📊 Category-Based Bulk Calls</h5>
            <p>Use category calls to initiate bulk calling campaigns:</p>
            <ul>
              <li><strong>All:</strong> Call all available leads</li>
              <li><strong>Active:</strong> Call leads marked as active</li>
              <li><strong>Scheduled:</strong> Call leads with scheduled follow-ups</li>
              <li>Other categories as needed for targeted campaigns</li>
            </ul>

            <h5 className="text-primary mb-3 mt-4">🤖 Automated Call Scheduling</h5>
            <p>The AI agent automatically manages the calling process:</p>
            <ul>
              <li>Analyzes call outcomes and schedules follow-ups based on results</li>
              <li>Forwards calls requiring human intervention</li>
              <li>Sends SMS and email notifications as needed</li>
              <li>Continues scheduling until calls are cancelled or marked as complete</li>
            </ul>

            <h5 className="text-primary mb-3 mt-4">⏰ Call Scheduling Rules</h5>
            <ul>
              <li><strong>Default:</strong> Calls are scheduled 2 weeks apart by default</li>
              <li><strong>Customer Time:</strong> Uses specific times provided by customers during calls</li>
              <li><strong>Manual Scheduling:</strong> Edit leads to set custom schedule times and categories</li>
              <li><strong>Specific Calls:</strong> Schedule individual calls by editing lead details</li>
            </ul>

            <Alert variant="info" className="mt-4">
              <strong>💡 Pro Tip:</strong> Always review extracted data from documents and monitor the Review Call Status page to track the progress of your review requests.
            </Alert>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInstructions(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LeadManagement;